import React, { useState } from 'react';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { dispatchService } from '../../../services/central-api/dispatch';
import DeleteModal from '../../../components/DeleteModal';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import { DispatchParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import CustomButton from '../../../components/CustomButton';
import OccurrenceTagModal from './OccurrenceTagModal';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';

interface TagsParams {
    navigation: Navigation;
}

export default function OccurrenceTags({ navigation }: TagsParams) {
    const route = useRoute<RouteProp<DispatchParamList, 'OccurrenceTags'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['OccurrenceTags']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedOccurrenceTag, setSelectedOccurrenceTag] = useState<OccurrenceTag>();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getOccurrenceTags', filters],
        queryFn: () => dispatchService.getOccurrenceTags(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    async function deleteTag(tag: OccurrenceTag) {
        try {
            await dispatchService.deleteOccurrenceTag(tag.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setIsDeleteModalVisible(false);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleFormClose() {
        setSelectedOccurrenceTag(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter,
                page: 0,
            }));
        }
    }

    return <Container>
        <Header
            title={translate('occurrenceTags')}
            actions={
                <CustomButton
                    text={translate('register')}
                    onPress={() => {
                        setSelectedOccurrenceTag(undefined);
                        setModalVisible(true);
                    }}
                />
            }
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredOccurrenceTags')}
                filter={<Filter
                    authenticatedUser={authenticatedUser}
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter} />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[
                        {
                            name: 'ID',
                            selector: row => row.id,
                            grow: 0
                        },
                        {
                            name: translate('color'),
                            grow: 0,
                            cell: row => <View style={{ backgroundColor: row.color, height: 20, width: 20, borderRadius: 4 }}></View>
                        },
                        {
                            name: translate('name'),
                            selector: row => row.name,
                            wrap: true,
                            grow: 4
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.actingBody.name,
                            wrap: true,
                        },
                        {
                            name: translate('associatedOccurrences'),
                            selector: row => row.associatedCount,
                            wrap: true,
                        },
                        {
                            cell: row => (
                                <DropdownButton
                                    actions={[
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedOccurrenceTag(row);
                                                setModalVisible(true);
                                            }
                                        },
                                        {
                                            content: translate('delete'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedOccurrenceTag(row);
                                                setIsDeleteModalVisible(true);
                                            },
                                        }
                                    ]}
                                />
                            ),
                            wrap: true,
                            button: true
                        }
                    ]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count ?? 0}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />
        </Card>


        <OccurrenceTagModal
            isModalVisible={isModalVisible}
            occurrenceTag={selectedOccurrenceTag}
            onClose={handleFormClose}
        />

        {selectedOccurrenceTag &&
            <DeleteModal
                isModalVisible={isDeleteModalVisible}
                itemName={selectedOccurrenceTag.name}
                setModalVisible={() => setIsDeleteModalVisible(false)}
                onCancel={() => {
                    setSelectedOccurrenceTag(undefined);
                }}
                onSubmit={async () => {
                    await deleteTag(selectedOccurrenceTag);
                }}
            />
        }

    </Container>;
}
